
import React from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useSettings } from "@/context/SettingsContext";
import ProfileButton from "../auth/ProfileButton";
import { useAuth } from "@/context/SafeAuthProvider";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import UnstuckLogo from "../UnstuckLogo";
import { getRpgThemeClasses, getRpgTerminology } from "@/lib/utils";
import SkipToContent from "../accessibility/SkipToContent";
import { useReducedMotion } from "@/hooks/useAccessibility";
import ErrorBoundary from "../ErrorBoundary";

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Apply reduced motion preferences
  useReducedMotion();
  
  console.log("AppLayout rendering:", { 
    user: user ? "authenticated" : "not authenticated", 
    pathname: location.pathname 
  });

  // Safe settings context access with error boundary
  let darkMode = false;
  let enableRpgMode = false;
  let rpgTheme = {
    background: "",
    accent: "",
    cardBackground: "",
    text: "",
    button: "",
    decorative: ""
  };

  try {
    const settings = useSettings();
    darkMode = settings.darkMode;
    enableRpgMode = settings.enableRpgMode;
    const themeClasses = getRpgThemeClasses(enableRpgMode, darkMode);
    // Ensure we always have all properties by merging with default structure
    rpgTheme = {
      background: themeClasses.background || "",
      accent: themeClasses.accent || "",
      cardBackground: themeClasses.cardBackground || "",
      text: themeClasses.text || "",
      button: themeClasses.button || "",
      decorative: themeClasses.decorative || ""
    };
  } catch (error) {
    console.warn("Settings context not available in AppLayout:", error);
    // Use fallback values (already set above)
  }
  
  // Determine current active tab based on path
  const currentPath = location.pathname === "/dashboard" ? "dashboard" : 
                      location.pathname === "/" ? "home" :
                      location.pathname === "/journal" ? "journal" :
                      location.pathname === "/history" ? "history" : 
                      user ? "dashboard" : "home";

  const handleLogoClick = () => {
    if (user) {
      navigate("/dashboard");
    } else {
      navigate("/");
    }
  };

  return (
    <div className={cn(
      "min-h-screen flex flex-col font-montserrat transition-colors duration-300",
      enableRpgMode ? (
        rpgTheme.background
      ) : (
        darkMode 
          ? "bg-gradient-to-br from-[#1a0930] to-[#3F2E46] text-[#FBF3F1]" 
          : "bg-gradient-to-br from-[#FBF3F1] to-[#D896A4]/10"
      ),
      enableRpgMode && "rpg-parchment"
    )}>
      {/* Skip to content link */}
      <SkipToContent />
      
      {/* Header */}
      <header 
        className={cn(
          "w-full py-4 px-6 flex justify-between items-center",
          enableRpgMode && "border-b border-amber-600/30"
        )}
        role="banner"
      >
        <div 
          onClick={handleLogoClick} 
          className="cursor-pointer"
          role="button"
          tabIndex={0}
          aria-label={user ? "Go to dashboard" : "Go to home page"}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleLogoClick();
            }
          }}
        >
          <UnstuckLogo />
        </div>
        
        <div>
          <ProfileButton />
        </div>
      </header>
      
      {/* Main Navigation for logged in users - exclude settings page */}
      {user && location.pathname !== "/settings" && (
        <nav 
          className="w-full flex justify-center mt-2 mb-6"
          role="navigation"
          aria-label="Main navigation"
        >
          <ErrorBoundary fallback={<div>Navigation temporarily unavailable</div>}>
            <Tabs value={currentPath} className="w-full max-w-md mx-auto">
              <TabsList className={cn(
                "grid grid-cols-3 w-full",
                enableRpgMode ? (
                  darkMode ? "bg-amber-900/40 border border-amber-600/40" : "bg-amber-100/60 border border-amber-400/40"
                ) : (
                  darkMode ? "bg-[#2d1a45]/50" : "bg-white/50"
                )
              )}>
                <TabsTrigger 
                  value="dashboard" 
                  onClick={() => navigate("/dashboard")}
                  className={cn(
                    enableRpgMode ? (
                      "data-[state=active]:bg-amber-600 data-[state=active]:text-white font-bold drop-shadow-sm"
                    ) : (
                      darkMode ? "data-[state=active]:bg-[#6127A0] data-[state=active]:text-white" : 
                      "data-[state=active]:bg-[#6127A0] data-[state=active]:text-white"
                    )
                  )}
                  aria-label={`Navigate to ${enableRpgMode ? getRpgTerminology("Dashboard") : "Dashboard"}`}
                >
                  {enableRpgMode ? getRpgTerminology("Dashboard") : "Dashboard"}
                </TabsTrigger>
                <TabsTrigger 
                  value="journal" 
                  onClick={() => navigate("/journal")}
                  className={cn(
                    enableRpgMode ? (
                      "data-[state=active]:bg-amber-600 data-[state=active]:text-white font-bold drop-shadow-sm"
                    ) : (
                      darkMode ? "data-[state=active]:bg-[#6127A0] data-[state=active]:text-white" : 
                      "data-[state=active]:bg-[#6127A0] data-[state=active]:text-white"
                    )
                  )}
                  aria-label={`Navigate to ${enableRpgMode ? getRpgTerminology("Journal") : "Journal"}`}
                >
                  {enableRpgMode ? getRpgTerminology("Journal") : "Journal"}
                </TabsTrigger>
                <TabsTrigger 
                  value="history" 
                  onClick={() => navigate("/history")}
                  className={cn(
                    enableRpgMode ? (
                      "data-[state=active]:bg-amber-600 data-[state=active]:text-white font-bold drop-shadow-sm"
                    ) : (
                      darkMode ? "data-[state=active]:bg-[#6127A0] data-[state=active]:text-white" : 
                      "data-[state=active]:bg-[#6127A0] data-[state=active]:text-white"
                    )
                  )}
                  aria-label={`Navigate to ${enableRpgMode ? "Chronicle" : "History"}`}
                >
                  {enableRpgMode ? "Chronicle" : "History"}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </ErrorBoundary>
        </nav>
      )}
      
      {/* Main Content */}
      <main 
        id="main-content"
        className="flex-grow flex flex-col items-center px-4 pb-6"
        role="main"
        aria-label="Main content"
      >
        {children}
      </main>
      
      {/* Footer - only show if not on settings page */}
      {location.pathname !== "/settings" && (
        <footer 
          className={cn(
            "mt-auto py-6 text-center text-sm flex flex-col items-center",
            enableRpgMode ? (
              rpgTheme.accent
            ) : (
              darkMode ? "text-[#D896A4]" : "text-[#003F6B]"
            )
          )}
          role="contentinfo"
        >
          <p className={enableRpgMode ? "drop-shadow-sm" : ""}>
            {enableRpgMode 
              ? "Unstuck: An ancient tool to break down barriers and forge new paths of adventure."
              : "Unstuck: A magic tool to break down walls and build new journeys."
            }
          </p>
          
          <p className={cn(
            "mt-1",
            enableRpgMode ? rpgTheme.accent : (darkMode ? "text-[#D896A4]" : "text-[#003F6B]")
          )}>
            Built with ❤️ by <a 
              href="https://ameliastewart.co" 
              target="_blank" 
              rel="noopener noreferrer"
              className={cn(
                "hover:underline transition-colors focus:outline-none focus:ring-2 focus:ring-current focus:ring-offset-2",
                enableRpgMode ? "text-amber-500 hover:text-amber-400" : (
                  darkMode 
                    ? "text-[#AE78F0] hover:text-[#c096ff]" 
                    : "text-[#6127A0] hover:text-[#4d1f80]"
                )
              )}
              aria-label="Visit Amelia Stewart's website (opens in new tab)"
            >
              Amelia Stewart
            </a>
          </p>
          
          <nav 
            className={cn(
              "mt-3 flex flex-wrap justify-center gap-4 text-xs",
              enableRpgMode ? rpgTheme.accent : (darkMode ? "text-[#D896A4]" : "text-[#003F6B]")
            )}
            aria-label="Footer navigation"
          >
            <a 
              href="/privacy" 
              className={cn(
                "hover:underline transition-colors focus:outline-none focus:ring-2 focus:ring-current focus:ring-offset-1",
                enableRpgMode ? "text-amber-500 hover:text-amber-400" : (
                  darkMode 
                    ? "text-[#AE78F0] hover:text-[#c096ff]" 
                    : "text-[#6127A0] hover:text-[#4d1f80]"
                )
              )}
            >
              Privacy Policy
            </a>
            <span aria-hidden="true">•</span>
            <a 
              href="/terms" 
              className={cn(
                "hover:underline transition-colors focus:outline-none focus:ring-2 focus:ring-current focus:ring-offset-1",
                enableRpgMode ? "text-amber-500 hover:text-amber-400" : (
                  darkMode 
                    ? "text-[#AE78F0] hover:text-[#c096ff]" 
                    : "text-[#6127A0] hover:text-[#4d1f80]"
                )
              )}
            >
              Terms of Service
            </a>
            <span aria-hidden="true">•</span>
            <a 
              href="/cookies" 
              className={cn(
                "hover:underline transition-colors focus:outline-none focus:ring-2 focus:ring-current focus:ring-offset-1",
                enableRpgMode ? "text-amber-500 hover:text-amber-400" : (
                  darkMode 
                    ? "text-[#AE78F0] hover:text-[#c096ff]" 
                    : "text-[#6127A0] hover:text-[#4d1f80]"
                )
              )}
            >
              Cookie Policy
            </a>
          </nav>
        </footer>
      )}
    </div>
  );
};

export default AppLayout;
